import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import { catchAll } from '@sorare/routing';

import {
  So5FixtureEvent,
  So5LeaderboardSeasonality,
  Sport,
  StoryState,
} from '#src/__generated__/globalTypes.ts';
import { PlayFootball } from '#src/atoms/icons/PlayFootball.tsx';
import { PlayMLB } from '#src/atoms/icons/PlayMLB.tsx';
import { PlayNBA } from '#src/atoms/icons/PlayNBA.tsx';
import { ProIcon } from '#src/atoms/icons/Pro.tsx';
import { ProModeIcon } from '#src/atoms/icons/ProModeIcon.tsx';
import { SeasonalityIcon } from '#src/atoms/icons/SeasonalityIcon/index.tsx';
import { Trophy } from '#src/atoms/icons/Trophy.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconEarthAmericas } from '#src/atoms/icons/system/iconEarthAmericas.ts';
import {
  ANY_SPORT_PLAY,
  ANY_SPORT_PLAY_BOARDS_ARENA_ARENAMODE,
  ANY_SPORT_PLAY_BOARDS_PRO,
  ANY_SPORT_PLAY_EVENTTYPE,
  ANY_SPORT_PLAY_EVENTTYPE_ARENA,
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS,
  ANY_SPORT_SERIES_PLAY,
  ANY_SPORT_SERIES_TRAINING,
} from '#src/constants/__generated__/routes.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { useIsInTraining } from '#src/hooks/commonGame/useIsInTraining.ts';
import { useSeriesNav } from '#src/hooks/commonGame/useSeriesNav.ts';
import { useEnableArenaClassicRooms } from '#src/hooks/useEnableArenaClassicRooms.ts';
import { useEnableSorare27Nav } from '#src/hooks/useEnableSorare27Nav.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { navLabels } from '#src/lib/glossary.ts';
import {
  generateSportPath,
  matchAnySportPath,
} from '#src/lib/routing/generateSportPath.ts';

import { NavItemWithSubNav } from '../NavItemWithSubNav/index.tsx';
import { useNavItemLayout } from '../useNavItemLayout.ts';

const ICONS = {
  [Sport.BASEBALL]: PlayMLB,
  [Sport.FOOTBALL]: PlayFootball,
  [Sport.NBA]: PlayNBA,
} as const;

type Props = { sport: Sport; sidebarCollapsed?: boolean };

export const PlayNavItem = ({ sport, sidebarCollapsed }: Props) => {
  const { formatMessage } = useIntl();
  const { isLaptopAndAbove, navItemsVariant, iconSize } = useNavItemLayout();
  const { pathname } = useLocation();
  const { so5 } = useConfigContext();
  const {
    flags: {
      enableProCareerMode = {
        baseball: false,
        football: false,
        nba: false,
      },
    },
  } = useFeatureFlags();

  const seriesNav = useSeriesNav();
  const isInTraining = useIsInTraining();
  const isSorare27Nav = useEnableSorare27Nav();
  const enableArenaClassicRooms = useEnableArenaClassicRooms();

  const PlayIcon = ICONS[sport];

  const playBadgeCount = {
    [Sport.FOOTBALL]:
      !seriesNav && so5?.myAvailableFixtureStory?.aasmState === StoryState.READY
        ? 1
        : 0,
    [Sport.BASEBALL]: 0,
    [Sport.NBA]: 0,
  }[sport];

  const getMainPlayLink = () => {
    if (isInTraining) {
      return generateSportPath(ANY_SPORT_SERIES_TRAINING, {
        sport,
      });
    }
    if (seriesNav) {
      return generateSportPath(ANY_SPORT_SERIES_PLAY, {
        sport,
      });
    }
    return generateSportPath(ANY_SPORT_PLAY, {
      sport,
    });
  };

  return (
    <NavItemWithSubNav
      item={{
        variant: navItemsVariant,
        icon: ({ isActive }) => <PlayIcon size={iconSize} active={isActive} />,
        label: formatMessage(navLabels.play),
        to: getMainPlayLink(),
        sidebarCollapsed,
        enableContextModeNavigation: true,
        enabledStoredLastNavigation: true,
        badge: playBadgeCount > 0 ? { content: playBadgeCount } : undefined,
        subItems: seriesNav
          ? []
          : isLaptopAndAbove && isSorare27Nav
            ? [
                {
                  icon: () => <ProModeIcon size={2} />,
                  label: formatMessage(navLabels.pro),
                  to: generateSportPath(ANY_SPORT_PLAY_BOARDS_PRO, { sport }),
                },
                {
                  icon: () => (
                    <SeasonalityIcon
                      sport={sport}
                      seasonality={So5LeaderboardSeasonality.IN_SEASON}
                      size={1.5}
                      color="currentColor"
                    />
                  ),
                  label: formatMessage(navLabels.arenaInSeason),
                  to: generateSportPath(ANY_SPORT_PLAY_BOARDS_ARENA_ARENAMODE, {
                    sport,
                    params: { arenaMode: 'cash' },
                  }),
                },
                {
                  icon: () => (
                    <SeasonalityIcon
                      sport={sport}
                      seasonality={So5LeaderboardSeasonality.ALL_SEASONS}
                      size={1.5}
                      color="currentColor"
                    />
                  ),
                  label: formatMessage(navLabels.arenaClassic),
                  to: generateSportPath(ANY_SPORT_PLAY_BOARDS_ARENA_ARENAMODE, {
                    sport,
                    params: { arenaMode: 'essence' },
                  }),
                },
                ...(enableArenaClassicRooms
                  ? [
                      {
                        icon: () => <Trophy size={2} />,
                        label: formatMessage(navLabels.arenaRooms),
                        to: generateSportPath(
                          ANY_SPORT_PLAY_BOARDS_ARENA_ARENAMODE,
                          {
                            sport,
                            params: { arenaMode: 'rooms' },
                          }
                        ),
                      },
                    ]
                  : []),
              ]
            : [
                ...(enableProCareerMode[sport.toLowerCase()]
                  ? [
                      {
                        icon: () => (
                          <FontAwesomeIcon icon={iconEarthAmericas} size="1x" />
                        ),
                        label: formatMessage(navLabels.globalCup),
                        to: generateSportPath(ANY_SPORT_PLAY_BOARDS_PRO, {
                          sport,
                        }),
                      },
                    ]
                  : []),
                {
                  icon: () => <ProIcon />,
                  label: formatMessage(navLabels.pro),
                  to: generateSportPath(ANY_SPORT_PLAY_EVENTTYPE, {
                    sport,
                    params: {
                      eventType: So5FixtureEvent.CLASSIC.toLowerCase(),
                    },
                  }),
                  controlledActive: !!matchAnySportPath(
                    catchAll(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS),
                    pathname
                  ),
                },
                {
                  icon: () => <Trophy size={2} />,
                  label: formatMessage(navLabels.leaderboards),
                  to: generateSportPath(ANY_SPORT_PLAY_EVENTTYPE_ARENA, {
                    sport,
                    params: {
                      eventType: 'classic',
                    },
                  }),
                },
              ],
      }}
    />
  );
};
